import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

export default function MessengerFloating({ usuario }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selThread, setSelThread] = useState(null); 
  // selThread: { id: string, type: 'evento'|'animal'|'p2p', nombre: string }
  const [msg, setMsg] = useState('');
  const chatEndRef = useRef(null);
  
  const [misEventos, setMisEventos] = useState([]);
  const [misAnimales, setMisAnimales] = useState([]);
  const [misChats, setMisChats] = useState([]);
  const [contactos, setContactos] = useState([]);

  const cargarDatos = useCallback(async () => {
    if (!usuario) return;
    try {
      const [rev, ra, rc, rchats] = await Promise.all([
        api.get('/eventos?todos=true'),
        api.get('/animales?todos=true'),
        api.get('/eventos/contactos'),
        api.get('/chats').catch(() => ({ data: [] })) // Respaldo por si falla la ruta nueva
      ]);
      
      const esAdmin = usuario.gradoPoder >= 4;
      
      setMisEventos(rev.data.filter(e => esAdmin || e.creadoPor?._id === usuario._id || e.creadoPor === usuario._id));
      setMisAnimales(ra.data.filter(a => esAdmin || a.creadoPor?._id === usuario._id || a.creadoPor === usuario._id));
      setMessengerContactos(rc.data, rchats.data);
      setMisChats(rchats.data);
    } catch {}
  }, [usuario]);

  const setMessengerContactos = (rawContactos, rawChats) => {
    // Unir contactos potenciales con chats existentes
    setContactos(rawContactos);
  };

  useEffect(() => {
    if (isOpen) {
      cargarDatos();
      const interval = setInterval(cargarDatos, 10000); 
      return () => clearInterval(interval);
    }
  }, [isOpen, cargarDatos]);

  useEffect(() => {
    if (isOpen && chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isOpen, selThread, misEventos, misAnimales, misChats]);

  // Encontrar el objeto de chat activo según el tipo
  const getActiveData = () => {
    if (!selThread) return null;
    if (selThread.type === 'evento') return misEventos.find(x => x._id === selThread.id);
    if (selThread.type === 'animal') return misAnimales.find(x => x._id === selThread.id);
    if (selThread.type === 'p2p') {
      const chat = misChats.find(x => x._id === selThread.id);
      if (chat) return { ...chat, comentarios: chat.mensajes.map(m => ({
        autor: chat.participantes.find(p => p._id === m.emisor)?.nombre || 'Usuario',
        texto: m.texto,
        fecha: m.fecha
      })) };
      return null;
    }
    return null;
  };

  const activeData = getActiveData();

  const handleSend = async (e) => {
    e.preventDefault();
    if (!msg.trim() || !selThread) return;
    try {
      if (selThread.type === 'p2p') {
        const chat = misChats.find(x => x._id === selThread.id);
        const receptorId = chat.participantes.find(p => p._id !== usuario._id)?._id;
        await api.post('/chats/enviar', { receptorId, texto: msg });
      } else {
        const endpoint = selThread.type === 'evento' 
          ? `/eventos/${selThread.id}/comentario` 
          : `/animales/${selThread.id}/comentario`;
        await api.post(endpoint, { texto: msg });
      }
      setMsg('');
      cargarDatos();
    } catch {
      toast.error('Error al enviar');
    }
  };

  const startP2P = async (contacto) => {
    try {
      const r = await api.post('/chats/enviar', { receptorId: contacto.id, texto: '¡Hola! Me gustaría coordinar contigo.' });
      setSelThread({ id: r.data._id, type: 'p2p', nombre: contacto.nombre });
      cargarDatos();
    } catch { toast.error('Error al iniciar chat'); }
  };

  if (!usuario) return null;

  const hasUnreadChat = misChats.some(c => 
    c.mensajes.some(m => m.emisor !== usuario._id && !m.leido)
  );

  const hasAlert = [...misEventos, ...misAnimales].some(x => {
    const last = x.comentarios?.[x.comentarios.length-1];
    return last && last.autor !== `${usuario.nombre || ''} ${usuario.apellido || ''}`.trim();
  }) || hasUnreadChat;

  // Auto-Open si hay mensajes nuevos (solo una vez al detectar)
  useEffect(() => {
    if (hasUnreadChat && !isOpen) {
      setIsOpen(true);
      // Opcional: seleccionar el primer chat no leido
      const firstUnread = misChats.find(c => c.mensajes.some(m => m.emisor !== usuario._id && !m.leido));
      if (firstUnread) {
        const otro = firstUnread.participantes.find(p => p._id !== usuario._id);
        setSelThread({ id: firstUnread._id, type: 'p2p', nombre: otro?.nombre });
      }
    }
  }, [hasUnreadChat, isOpen, misChats, usuario._id]);

  // Marcar como leido al abrir un hilo P2P
  useEffect(() => {
    if (selThread?.type === 'p2p') {
      api.put(`/chats/${selThread.id}/leer`).then(() => cargarDatos()).catch(() => {});
    }
  }, [selThread, cargarDatos]);

  return (
    <div className={`messenger-wrapper ${isOpen ? 'open' : ''}`}>
      {!isOpen ? (
        <button className="messenger-trigger" onClick={() => setIsOpen(true)}>
          <span className="msg-ico">💬</span>
          <span className="msg-txt">Comunicación</span>
          {hasAlert && <div className="dot-pulse-msg"></div>}
        </button>
      ) : (
        <div className="messenger-box card shadow-lg">
          <div className="msg-header">
            <h3>💬 Mensajero En Vivo</h3>
            <button className="msg-close" onClick={() => setIsOpen(false)}>✕</button>
          </div>

          <div className="msg-body">
            <div className="msg-sidebar">
              
              <div className="msg-sec-label">📋 COORDINACIÓN (REVISIÓN)</div>
              {misEventos.map(e => (
                <div key={e._id} className={`msg-thread-item ${selThread?.id === e._id ? 'active' : ''}`} onClick={() => setSelThread({id:e._id, type:'evento', nombre:e.nombre})}>
                  <div className="thread-tit">🗓️ {e.nombre}</div>
                  <div className="thread-sub">{e.validado ? '✅ Publicada' : '⏳ Moderación'}</div>
                </div>
              ))}
              {misAnimales.map(a => (
                <div key={a._id} className={`msg-thread-item ${selThread?.id === a._id ? 'active' : ''}`} onClick={() => setSelThread({id:a._id, type:'animal', nombre:a.nombre})}>
                  <div className="thread-tit">🐾 {a.nombre}</div>
                  <div className="thread-sub">{a.validado ? '✅ Publicada' : '⏳ Moderación'}</div>
                </div>
              ))}

              <div className="msg-sec-label mt-4">🤝 CHATS DIRECTOS</div>
              {/* Chats activos */}
              {misChats.map(c => {
                const otro = c.participantes.find(p => p._id !== usuario._id);
                const unread = c.mensajes.some(m => m.emisor !== usuario._id && !m.leido);
                return (
                  <div key={c._id} className={`msg-thread-item ${selThread?.id === c._id ? 'active' : ''} ${unread ? 'unread' : ''}`} onClick={() => setSelThread({id:c._id, type:'p2p', nombre:otro?.nombre})}>
                    <div className="thread-tit">👤 {otro?.nombre} {otro?.apellido} {unread && '🔴'}</div>
                    <div className="thread-sub">{otro?.rol}</div>
                  </div>
                );
              })}
              {/* Sugerencias de contacto (sin chat aún) */}
              {contactos.filter(c => !misChats.some(chat => chat.participantes.some(p => p._id === c.id))).map(c => (
                <div key={c.id} className="msg-thread-item opacity-70" onClick={() => startP2P(c)}>
                  <div className="thread-tit">➕ {c.nombre}</div>
                  <div className="thread-sub">{c.tipo} (Iniciar)</div>
                </div>
              ))}
            </div>

            <div className="msg-chat-area">
              {activeData ? (
                <>
                  <div className="chat-history">
                    {activeData.comentarios?.length > 0 ? (
                      activeData.comentarios.map((c, i) => (
                        <div key={i} className={`chat-bubble ${c.autor.includes(usuario.nombre) ? 'me' : 'them'}`}>
                          <div className="chat-meta">{c.autor}</div>
                          <div className="chat-text">{c.texto}</div>
                          <div className="chat-time">{new Date(c.fecha).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                        </div>
                      ))
                    ) : (
                      <div className="chat-empty">Inicia la conversación directa.</div>
                    )}
                    <div ref={chatEndRef} />
                  </div>
                  <form className="chat-input-row" onSubmit={handleSend}>
                    <input className="input" placeholder="Escribe un mensaje..." value={msg} onChange={e => setMsg(e.target.value)} />
                    <button type="submit" className="btn btn-v btn-icon">🚀</button>
                  </form>
                </>
              ) : (
                <div className="chat-select-prompt">
                   <p>Selecciona una propuesta de coordinación o un contacto directo para chatear.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
